import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { Progress } from 'semantic-ui-react'
import axios, { CancelTokenSource } from 'axios'
import { ClimaProgramService } from '~/api/services/climaProgram'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import FormPart from './FormPart'
import { RouteTypes } from '~/routes/constants'
import { handleErrors } from '~/utils/helpers'
import { ClimaProgramQuery } from '~/QueryHooks/ClimaProgramQuery'
import { createPurchaseFormData, FeedPurchaseData } from './utils'
import { ContactInfo, InfoText, WelcomeHeader } from '~/pages/Manager/Profile/RegistrationTab/AddRegistration/style'

const ACTIVE_YEAR = new Date().getFullYear()

const CreateEditPurchases: React.FC = () => {
  const { t } = useTranslation()
  const { idFarmer, id } = useParams()
  const navigate = useNavigate()

  const [isBusy, setIsBusy] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const cancelTokenRef = useRef<CancelTokenSource | null>(null)

  const methods = useForm({
    mode: 'onBlur'
  })

  const {
    data: purchasesOptionData,
    isLoading: isLoadingData
  } = ClimaProgramQuery.getPurchasesOptionsData()

  useEffect(() => {
    if(id) {
      fetchFeedPurchases()
    }
  }, [id])

  async function fetchFeedPurchases() {
    setIsBusy(true)
    try {
      const response = await ClimaProgramService.editPurchase(id)
      methods.reset(response.data)
    } catch(error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data: FeedPurchaseData) {
    if(!data.purchaseReceiptFile) {
      notify(t('Please add feed purchase receipt'), 'error')
      return
    }
    data.farmerId = idFarmer
    const formData = createPurchaseFormData(data)
    const source = axios.CancelToken.source()
    cancelTokenRef.current = source
    setIsUploading(true)
    setUploadProgress(0)

    try {
      if (id) {
        await ClimaProgramService.updatePurchase({
          id: id,
          formData,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent
            if (total) {
              setUploadProgress(Math.round((loaded / total) * 100))
            }
          },
          cancelToken: source
        })
        notify(t('Purchase updated successfully'), 'success')
      } else {
        await ClimaProgramService.createPurchase({
          formData,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent
            if (total) {
              setUploadProgress(Math.round((loaded / total) * 100))
            }
          },
          cancelToken: source
        })
        notify(t('Purchase created successfully'), 'success')
      }
      navigate(RouteTypes.MANAGER_CLIMATE_PROGRAM_PURCHASES)
    } catch (err) {
      if (axios.isCancel(err)) {
        notify(t('Upload canceled'), 'error')
      } else {
        notify(err?.response?.data?.message || t('General error'), 'error')
      }
    }
    setIsUploading(false)
    setUploadProgress(0)
  }

  function renderHeader() {
    return (
      <>
        <ContactInfo>
          {t('clima_contact_info')}
        </ContactInfo>

        <WelcomeHeader as='h1'>
          {id ? t('Edit Purchase') : t('New Purchase')}, {t('Period')} {ACTIVE_YEAR}
        </WelcomeHeader>

        <InfoText>
          {t('Please upload your feed order receipts and provide the feeding period information.')}
        </InfoText>
      </>
    )
  }

  function renderActions() {
    return <El.PageHeaderActionButton>{t('Submit purchase')}</El.PageHeaderActionButton>
  }

  function renderForm() {
    return <FormPart options={purchasesOptionData}/>
  }

  function renderProgressBar() {
    return <Progress
      percent={uploadProgress}
      indicating
      progress
      style={{ marginBottom: '1rem' }}
    />
  }

  return (
    <Layout
      title={t('Add purchase')}
      segmented
      isBusy={isLoadingData || isBusy}
      actions={renderActions()}
      onSubmit={methods.handleSubmit(onSubmit)}
      close={RouteTypes.MANAGER_CLIMATE_PROGRAM_PURCHASES}
    >
      {renderHeader()}

      {isUploading && renderProgressBar()}
      <FormProvider {...methods}>
        {renderForm()}
      </FormProvider>
    </Layout>
  )
}

export default CreateEditPurchases
